// =============================================================================
// T-421 (AC-1) — « Aides possibles par client » (Hub Conformité, TEF)
// Une ligne par client, une colonne par aide, une pastille par case. Lecture seule.
// Source : GET /api/aides/eligibilite/clients (le MÊME verdict que « Détecter les aides »).
// ⛔ Données de santé : l'écran et l'export ne montrent que des verdicts et des libellés de
//    manquants — jamais une valeur de GIR, de handicap ou de revenu (le serveur ne les envoie pas).
// =============================================================================
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Sparkles, ChevronDown, ChevronUp, CheckCircle2, HelpCircle, Landmark, ExternalLink, Download, Users } from "lucide-react";
import { OHGrid } from "../../components/ui/oh-grid";
import { TintedBadge } from "../../components/ui/oh-tinted";
import { API, getAuthHeaders } from "../metier/dossier/helpers";
import { formatDateFR } from "../../utils/dateFormat";

export const VERDICTS = {
  eligible: { label: "Éligible", color: "green", rang: 0 },
  indetermine: { label: "À vérifier", color: "amber", rang: 1 },
  non_eligible: { label: "Non éligible", color: "gray", rang: 2 },
};
export const CAISSE = "Caisse de retraite";
const TAILLE_PAGE = 200;
const LIMITE = 2000;

const MESSAGE_ERREUR = {
  401: "Session expirée : reconnectez-vous pour voir les aides par client.",
  403: "Votre rôle n'a pas accès aux aides possibles par client.",
};

const verdictDe = (l, aideId) => l.aides?.[aideId]?.verdict;
const libelleVerdict = (v) => VERDICTS[v]?.label || "—";

// Texte de survol : la règle citée et les données manquantes (jamais de valeur lue).
export function titreCase(case_) {
  if (!case_) return "";
  const manquants = case_.manquants?.length ? ` Manque : ${case_.manquants.join(", ")}.` : "";
  return `${case_.raison || ""}${manquants}`.trim();
}

// Libellé court d'une aide (« APA », « CNAV »…) : le nom complet reste dans le survol et dans le CSV.
// ⛔ Des libellés longs élargissaient la colonne des libellés des cartes OHGrid à 390 px : les pastilles
//    sortaient de la carte et étaient rognées (T-421T).
export const nomCourt = (a) => String(a?.nom || a?.id || "").split(" — ")[0];

// Écran étroit (< md = 768 px, le point de bascule carte/tableau d'OHGrid). Sans matchMedia (jsdom) : large.
const REQUETE_MOBILE = "(max-width: 767px)";
function useEcranEtroit() {
  const lire = () => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(REQUETE_MOBILE).matches : false);
  const [etroit, setEtroit] = useState(lire);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(REQUETE_MOBILE);
    const maj = () => setEtroit(mq.matches);
    maj();
    mq.addEventListener?.("change", maj);
    return () => mq.removeEventListener?.("change", maj);
  }, []);
  return etroit;
}

const pluriel = (n, un, plusieurs) => `${n} ${n > 1 ? plusieurs : un}`;

// Carte mobile : « N éligibles · M à vérifier », les pastilles des aides éligibles / à vérifier (repliées),
// un appui ouvre le détail par aide (raison, données manquantes, pièces). Verdicts et libellés seulement.
export function ResumeAides({ ligne, aides }) {
  const [ouvert, setOuvert] = useState(false);
  const retenues = aides.filter((a) => ["eligible", "indetermine"].includes(verdictDe(ligne, a.id)));
  const piecesDe = (id) => (ligne.pieces_a_fournir || []).find((p) => p.aide_id === id);
  return (
    <div className="w-full min-w-0 text-left space-y-2" data-testid={`resume-aides-${ligne.id}`}>
      <p className="text-sm" style={{ color: "var(--oh-text)" }} data-testid={`resume-compteurs-${ligne.id}`}>
        {pluriel(ligne.nb_eligibles || 0, "éligible", "éligibles")} · {ligne.nb_a_verifier || 0} à vérifier
      </p>
      {retenues.length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid={`resume-pastilles-${ligne.id}`}>
          {retenues.map((a) => {
            const v = VERDICTS[verdictDe(ligne, a.id)];
            return (
              <span key={a.id} title={`${a.nom || a.id} — ${titreCase(ligne.aides[a.id])}`} data-testid={`pastille-${a.id}`}>
                <TintedBadge color={v.color}>{nomCourt(a)} · {v.label}</TintedBadge>
              </span>
            );
          })}
        </div>
      )}
      <button type="button" aria-expanded={ouvert} onClick={() => setOuvert((o) => !o)}
        data-testid={`resume-bascule-${ligne.id}`}
        className="inline-flex items-center gap-1 min-h-[44px] text-sm"
        style={{ color: "var(--oh-accent-text)" }}>
        {ouvert ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        {ouvert ? "Masquer le détail" : "Voir le détail par aide"}
      </button>
      {ouvert && (
        <ul className="space-y-2" data-testid={`detail-aides-${ligne.id}`}>
          {aides.map((a) => {
            const c = ligne.aides?.[a.id];
            const v = VERDICTS[c?.verdict];
            const p = piecesDe(a.id);
            return (
              <li key={a.id} className="rounded-lg p-2 text-xs normal-case tracking-normal"
                style={{ border: "1px solid var(--oh-border)", background: "var(--oh-bg-elevated)" }}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium" style={{ color: "var(--oh-text)" }} title={a.nom}>{nomCourt(a)}</span>
                  {v && <TintedBadge color={v.color}>{v.label}</TintedBadge>}
                </div>
                {c && <p className="mt-1 break-words" style={{ color: "var(--oh-text-secondary)" }}>{titreCase(c)}</p>}
                {p && (p.documents || []).length > 0 && (
                  <p className="mt-1 break-words" style={{ color: "var(--oh-text-secondary)" }}>
                    Pièces à fournir (d'après le catalogue) : {p.documents.join(", ")}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const resumePieces = (l) =>
  (l.pieces_a_fournir || []).map((p) => `${p.aide_nom} : ${(p.documents || []).join(", ")}`).join(" | ");

// Export CSV — même forme que HubGrid : BOM, séparateur « ; », guillemets doublés.
// Verdicts et libellés de manquants SEULEMENT.
export function construireCsv(rows, aides) {
  const colonnes = [
    ["Client", (r) => r.client],
    ["Dossier", (r) => r.dossier_reference],
    ...aides.map((a) => [a.nom, (r) => libelleVerdict(verdictDe(r, a.id))]),
    ["Aides éligibles", (r) => r.nb_eligibles],
    ["Aides à vérifier", (r) => r.nb_a_verifier],
    ["À vérifier : données manquantes", (r) => (r.manquants || []).join(", ")],
    ["Pièces à fournir (d'après le catalogue)", resumePieces],
  ];
  const q = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lignes = rows.map((r) => colonnes.map(([, f]) => q(f(r))).join(";"));
  return "﻿" + [colonnes.map(([t]) => q(t)).join(";"), ...lignes].join("\n");
}

function telecharger(rows, aides) {
  const blob = new Blob([construireCsv(rows, aides)], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `aides_possibles_par_client_${new Date().toISOString().split("T")[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AidesParClientPage({ tenantId, onOpenDossier }) {
  const [lignes, setLignes] = useState([]);
  const [aides, setAides] = useState([]);
  const [tronque, setTronque] = useState(false);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const visibles = useRef([]);
  const surLignesVisibles = useCallback((rows) => { visibles.current = rows; }, []);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      let page = 1;
      let toutes = [];
      let entete = [];
      let total = 0;
      do {
        const res = await fetch(`${API}/api/aides/eligibilite/clients?page=${page}&taille=${TAILLE_PAGE}`, {
          headers: getAuthHeaders(tenantId),
          credentials: "include",
        });
        if (!res.ok) {
          setLignes([]);
          setAides([]);
          setErreur(MESSAGE_ERREUR[res.status] || `Chargement impossible (erreur ${res.status}).`);
          return;
        }
        const data = await res.json();
        entete = Array.isArray(data.aides) ? data.aides : entete;
        toutes = toutes.concat(Array.isArray(data.lignes) ? data.lignes : []);
        total = Number(data.total || 0);
        if (!data.lignes?.length) break;
        page += 1;
      } while (toutes.length < total && toutes.length < LIMITE);
      setAides(entete);
      setLignes(toutes);
      setTronque(total > toutes.length);
    } catch {
      setLignes([]);
      setAides([]);
      setErreur("Chargement impossible : le serveur ne répond pas.");
    } finally {
      setChargement(false);
    }
  }, [tenantId]);

  useEffect(() => { charger(); }, [charger]);

  const etroit = useEcranEtroit();

  // Tableau (≥ md) : une colonne par aide, libellé COURT. Carte (< md) : une seule entrée « Aides » (résumé).
  const colonnesAides = useMemo(() => (etroit
    ? [{ key: "aides_resume", label: "Aides", sortable: false,
         render: (_v, r) => <ResumeAides ligne={r} aides={aides} /> }]
    : aides.map((a) => ({
      key: `aide_${a.id}`,
      label: nomCourt(a),
      sortValue: (r) => VERDICTS[verdictDe(r, a.id)]?.rang ?? 9,
      filter: { type: "select", value: (r) => verdictDe(r, a.id) || "",
                options: Object.entries(VERDICTS).map(([value, e]) => ({ value, label: e.label })) },
      render: (_v, r) => {
        const c = r.aides?.[a.id];
        const v = VERDICTS[c?.verdict];
        return v
          ? <span title={`${a.nom || a.id} — ${titreCase(c)}`} data-testid={`pastille-${a.id}`}><TintedBadge color={v.color}>{v.label}</TintedBadge></span>
          : "—";
      },
    }))), [aides, etroit]);

  const columns = useMemo(() => [
    { key: "client", label: "Client",
      filter: { type: "text", placeholder: "Client…" },
      render: (v, r) => (
        <span style={{ color: "var(--oh-text)" }}>
          {v || "—"}
          {r.dossier_reference && (
            <span className="block text-xs" style={{ color: "var(--oh-text-muted)" }}>{r.dossier_reference}</span>
          )}
        </span>
      ) },
    ...colonnesAides,
    ...(etroit ? [] : [{ key: "pieces_a_fournir", label: "Pièces à fournir",
      sortValue: (r) => (r.pieces_a_fournir || []).length,
      render: (_v, r) => {
        const p = r.pieces_a_fournir || [];
        if (!p.length) return <span style={{ color: "var(--oh-text-muted)" }}>—</span>;
        return (
          <span className="text-xs" style={{ color: "var(--oh-text-secondary)" }} title={resumePieces(r)}>
            {p.length} aide{p.length > 1 ? "s" : ""} · {p.reduce((n, x) => n + (x.documents || []).length, 0)} pièces
            <span className="block" style={{ color: "var(--oh-text-muted)" }}>d'après le catalogue</span>
          </span>
        );
      } }]),
    { key: "created_at", label: "Client depuis", render: (v) => formatDateFR(v) || "—" },
  ], [colonnesAides, etroit]);

  const stats = useMemo(() => [
    { key: "total", label: "Clients", value: lignes.length, icon: Users, color: "blue", filter: () => true },
    { key: "eligible", label: "Au moins une éligible", icon: CheckCircle2, color: "green",
      value: lignes.filter((l) => l.nb_eligibles > 0).length, filter: (l) => l.nb_eligibles > 0 },
    { key: "a_verifier", label: "Au moins une à vérifier", icon: HelpCircle, color: "amber",
      value: lignes.filter((l) => l.nb_a_verifier > 0).length, filter: (l) => l.nb_a_verifier > 0 },
    { key: "caisse", label: "À vérifier : caisse de retraite", icon: Landmark, color: "orange",
      value: lignes.filter((l) => (l.manquants || []).includes(CAISSE)).length,
      filter: (l) => (l.manquants || []).includes(CAISSE) },
  ], [lignes]);

  const rowActions = useMemo(() => (onOpenDossier
    ? [{ label: "Ouvrir le dossier", icon: ExternalLink, onClick: (l) => onOpenDossier(l.dossier_id),
         show: (l) => !!l.dossier_id }]
    : []), [onOpenDossier]);

  const bulkActions = useMemo(() => [
    { label: (n) => `Exporter la sélection (${n})`, icon: Download,
      onClick: (ids) => telecharger(lignes.filter((l) => ids.includes(l.id)), aides) },
  ], [lignes, aides]);

  const headerActions = useMemo(() => [
    { label: "Exporter (CSV)", icon: Download, onClick: () => telecharger(visibles.current, aides) },
  ], [aides]);

  return (
    <div className="space-y-4 p-4" data-testid="aides-par-client-page">
      <div className="flex items-center gap-3">
        <Sparkles className="w-5 h-5" style={{ color: "var(--oh-accent-text)" }} />
        <div>
          <h1 className="text-lg font-medium" style={{ color: "var(--oh-text)" }}>Aides possibles par client</h1>
          <p className="text-sm" style={{ color: "var(--oh-text-secondary)" }} data-testid="aides-par-client-phrase">
            Pour chaque client, les aides complémentaires auxquelles il peut prétendre d'après son dossier le plus récent :
            « À vérifier » veut dire qu'il manque une information pour conclure.
          </p>
        </div>
      </div>

      {erreur && (
        <div role="alert" data-testid="aides-par-client-erreur" className="rounded-lg px-4 py-3 text-sm"
          style={{ background: "var(--oh-error-bg)", color: "var(--oh-error-text)", border: "1px solid var(--oh-error-border)" }}>
          {erreur}
        </div>
      )}
      {tronque && (
        <div role="status" data-testid="aides-par-client-tronque" className="rounded-lg px-4 py-3 text-sm"
          style={{ background: "var(--oh-info-bg)", color: "var(--oh-info-text)", border: "1px solid var(--oh-info-border)" }}>
          La liste est limitée aux clients les plus récents : les plus anciens ne sont pas affichés.
        </div>
      )}

      <OHGrid
        testIdPrefix="aides-par-client"
        data={lignes}
        columns={columns}
        stats={stats}
        loading={chargement}
        onRefresh={charger}
        defaultSortKey="created_at"
        defaultSortDir="desc"
        searchPlaceholder="Rechercher un client ou un dossier…"
        searchFilter={(l, q) => `${l.client} ${l.dossier_reference}`.toLowerCase().includes(q)}
        emptyMessage={erreur ? "Aucune donnée à afficher" : "Aucun client pour le moment"}
        rowActions={rowActions}
        selectable
        bulkActions={bulkActions}
        headerActions={headerActions}
        onRowsChange={surLignesVisibles}
        pagination
        pageSize={50}
      />
    </div>
  );
}
